import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { ImageWithFallback } from './ui/ImageWithFallback';
import { toast } from 'sonner';
import api from 'Common/api';

interface ImageUploaderProps {

  onImagesChange: (images: string[]) => void;
  maxImages?: number;
  urls:any[];
  path:any
}

export function ImageUploader({urls, path, onImagesChange, maxImages = 10 }: ImageUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [images,setImages]=useState<any[]>([])
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const availableSlots = maxImages - images.length;
    if (availableSlots <= 0) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, availableSlots);
    if (files.length > availableSlots) {
      toast.error(`You can add ${availableSlots} more image${availableSlots === 1 ? '' : 's'}`);
    }

    setIsUploading(true);

    try {
      const uploadedUrls: string[] = [];

      for (const file of filesToUpload) {

        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} is too large. Max size is 5MB`);
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);
        try {
          const response: any = await api.post(`/proposal/upload/${path}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          const data = Array.isArray(response) ? response : response?.data;
          const uploadedUrl = data?.[0]?.url;
          if (uploadedUrl) {
            uploadedUrls.push(uploadedUrl);
          }
        } catch {
          toast.error(`Could not upload ${file.name}`);
        }
      }

      if (uploadedUrls.length > 0) {
        const nextImages = [...images, ...uploadedUrls].slice(0, maxImages);
        setImages(nextImages);
        onImagesChange(nextImages);
        toast.success(`${uploadedUrls.length} image${uploadedUrls.length === 1 ? '' : 's'} uploaded successfully`);
      }
    } catch (error) {
      toast.error('Failed to upload images');
      console.error(error);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    onImagesChange(updatedImages);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  useEffect(()=>{
  setImages(urls)
  },[urls])

  return (
    <div className="space-y-4">
      {/* Drag and Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        className={`
          relative border-2 border-dashed rounded-xl transition-all cursor-pointer
          ${isDragging 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-200 hover:border-gray-300 bg-white'
          }
          ${isUploading ? 'opacity-50 pointer-events-none' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleFileUpload(e.target.files)}
          className="hidden"
        />
        
        <div className="px-6 py-10 text-center">
          <div className="inline-flex p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full mb-4">
            <Upload className={`size-8 text-blue-600 ${isUploading ? 'animate-bounce' : ''}`} />
          </div>
          
          <h3 className="mb-2">
            {isUploading ? 'Uploading images...' : 'Drop images here or click to browse'}
          </h3>
          
          <p className="text-sm text-muted-foreground mb-4">
            Supports JPG, PNG, GIF up to 5MB each
          </p>
          
          {images.length > 0 && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm">
              <ImageIcon className="size-4" />
              {images.length} / {maxImages} images added
            </div>
          )}
        </div>
      </div>

      {/* Existing Images Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          {images.map((img, index) => (
            <div 
              key={index} 
              className="relative aspect-square group rounded-lg overflow-hidden bg-gray-100 border border-gray-200 hover:shadow-lg transition-all"
            >
              <ImageWithFallback
                src={img}
                alt={`Property ${index + 1}`}
                className="w-full h-full object-cover"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0  group-hover:bg-black/40 transition-all" />
              
              {/* Delete Button */}
              <Button
                type="button"
                size="sm"
                className="absolute top-2 right-2 size-8 p-0 bg-white text-red-600 hover:text-red-700  group-hover:opacity-100 transition-all shadow-lg"
                onClick={() => handleRemoveImage(index)}
              >
                <X className="size-4" />
              </Button>
              
              {/* Cover Badge */}
              {index === 0 && (
                <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2.5 py-1 rounded-full text-xs shadow-lg">
                  <ImageIcon className="size-3" />
                  Cover Photo
                </div>
              )}
              
              {/* Image Number */}
              <div className="absolute top-2 left-2 bg-black/70 text-white size-6 rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-all">
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* URL Input - Modern Design */}
   

      {images.length >= maxImages && (
        <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="p-2 bg-amber-100 rounded-lg">
            <ImageIcon className="size-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-amber-900">
              Maximum of {maxImages} images reached
            </p>
            <p className="text-xs text-amber-700">
              Remove some images to add more
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
